import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage } from '../../hooks/useAsync';
import { OrgPicker } from '../../components/OrgPicker';
import { Alert, Button, Card, Checkbox, Field, Input, PageTitle, Select } from '../../components/ui';
import type { OrganizationAdmin } from '../../types/api';

export function UserNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [locale, setLocale] = useState('en');
  const [org, setOrg] = useState<OrganizationAdmin | null>(null);
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [isStaff, setIsStaff] = useState(false);
  const [sendReset, setSendReset] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = await api.createUser({ email: email.trim(), first_name: firstName, last_name: lastName, locale, organization_id: org?.id ?? null, role, is_staff: isStaff, send_reset: sendReset });
      navigate(`/users/${user.id}`, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <PageTitle title={t('users.new')} subtitle={t('users.newSubtitle')} />
      <form onSubmit={submit}>
        <Card>
          <div className="flex flex-col gap-3">
            <Field label={t('users.email')}>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('users.firstName')}>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Field>
              <Field label={t('users.lastName')}>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Field>
            </div>
            <Field label={t('users.locale')} className="w-40">
              <Select value={locale} onChange={(e) => setLocale(e.target.value)}>
                <option value="en">English</option>
                <option value="fr">Français</option>
              </Select>
            </Field>
            <Field label={t('users.organization')} hint={t('users.organizationHint')}>
              <OrgPicker value={org} onChange={setOrg} allowNone />
            </Field>
            {org && (
              <Field label={t('orgs.roleLabel')} className="w-40">
                <Select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'member')}>
                  <option value="member">{t('orgs.role.member')}</option>
                  <option value="admin">{t('orgs.role.admin')}</option>
                </Select>
              </Field>
            )}
            <Checkbox label={t('users.makeStaff')} checked={isStaff} onChange={(e) => setIsStaff(e.target.checked)} />
            <Checkbox label={t('users.sendReset')} checked={sendReset} onChange={(e) => setSendReset(e.target.checked)} />
            {error && <Alert>{error}</Alert>}
            <div className="flex justify-end gap-2">
              <Button onClick={() => navigate(-1)}>{t('common.cancel')}</Button>
              <Button type="submit" variant="primary" busy={busy}>
                {t('common.create')}
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
}
